import type { Bootstrap } from './types';
import { useAppStore } from './store';

const base = import.meta.env.VITE_API_BASE_URL ?? '/api/mock/v1';
const fallbackFiles: Record<string, string> = {
  config: 'app-config.json', navigation: 'ui-navigation.json', features: 'feature-registry.json', roles: 'roles.json', sources: 'data-sources.json',
  layers: 'map-layers.json', sensors: 'sensors.json', observations: 'observations.json', cameras: 'cameras.json', drones: 'drones.json', alerts: 'alerts.json',
  incidents: 'incidents.json', weather: 'weather.json', riskCells: 'risk-cells.geojson', resources: 'resources.json', missions: 'missions.json', simulations: 'simulations.json',
  arrivalTimeSurface: 'arrival-time-surface.geojson', recommendations: 'recommendations.json', tasks: 'tasks.json', notifications: 'notifications.json',
  help: 'help-topics.json', scenarios: 'demo-scenarios.json', incidentStories: 'incident-timeline-stories.json', layouts: 'dashboard-layouts.json'
};

export async function getBootstrap(): Promise<Bootstrap> {
  try {
    const response = await fetch(`${base}/bootstrap`);
    if (!response.ok) throw new Error('API unavailable');
    return response.json();
  } catch {
    const data = await Promise.all(Object.entries(fallbackFiles).map(async ([key, file]) => [key, await (await fetch(`/${file}`)).json()]));
    return { ...Object.fromEntries(data), scenarioRuntime: { scenarioId: 'pilot-alpha', scenarioTime: '2026-09-02T14:30:00Z', playing: false, speed: 1 }, audit: [], fallbackMode: true } as Bootstrap;
  }
}

export class ApiError extends Error {
  constructor(message: string, public status: number, public code?: string) { super(message); this.name = 'ApiError'; }
}

export async function apiQuery(path: string) {
  const { role, userId } = useAppStore.getState();
  const response = await fetch(`${base}${path}`, { headers: { 'x-user-role': role, 'x-user-id': userId } });
  const value = await response.json().catch(() => ({}));
  if (!response.ok) throw new ApiError(value.error?.message ?? 'The mock operational record could not be loaded.', response.status, value.error?.code);
  return value;
}

export async function apiMutation(path: string, method = 'POST', body?: unknown) {
  const { role, userId } = useAppStore.getState();
  const response = await fetch(`${base}${path}`, { method, headers: { 'Content-Type': 'application/json', 'x-user-role': role, 'x-user-id': userId }, body: body === undefined ? undefined : JSON.stringify(body) });
  const value = await response.json().catch(() => ({}));
  if (!response.ok) throw new ApiError(value.error?.message ?? 'The simulated action could not be completed.', response.status, value.error?.code);
  return value;
}

export function connectEvents(onEvent: (event: any) => void) {
  const stream = new EventSource(`${base}/events`);
  stream.addEventListener('domain', (message) => onEvent(JSON.parse((message as MessageEvent).data)));
  return () => stream.close();
}
